/**
 * PATH: packages/ui/src/components/settings/organisation-tab/OrgFormFields.tsx
 * FILE_ID: UI-SETTINGS-FORM-001
 * PURPOSE: Presentational form fields for organisation settings
 * WHY: Extracted from OrganisationTab.tsx to keep the orchestrator under 300 lines
 * FLOW:
 * ┌──────────────┐   ┌────────────────┐   ┌──────────────────┐
 * │ formData +   │ → │ OrgFormFields  │ → │ Rendered form    │
 * │ onChange     │   │ (presentational)│   │ sections + inputs│
 * └──────────────┘   └────────────────┘   └──────────────────┘
 * DEPENDENCIES:
 *   - lucide-react: icons for form field adornments
 *   - ../../lib/utils: cn() utility for conditional classNames
 *   - ./organisation-tab-types: FormInputProps, OrgFormFieldsProps
 */

import { useMemo } from 'react'
import { Building2, Globe, Mail, Phone, MapPin } from 'lucide-react'
import { cn } from '../../../lib/utils'
import type { FormInputProps, OrgFormFieldsProps } from './organisation-tab-types'

function validateAbn(value: string | undefined): string | undefined {
  if (!value) return undefined
  const digits = value.replace(/[\s-]/g, '')
  if (!digits) return undefined
  if (!/^\d*$/.test(digits)) return 'ABN must contain digits only'
  if (digits.length !== 11) return `ABN must be exactly 11 digits (${digits.length}/11)`
  if (digits[0] === '0') return 'ABN cannot start with 0'
  return undefined
}

function validateAcn(value: string | undefined): string | undefined {
  if (!value) return undefined
  const digits = value.replace(/[\s-]/g, '')
  if (!digits) return undefined
  if (!/^\d*$/.test(digits)) return 'ACN must contain digits only'
  if (digits.length !== 9) return `ACN must be exactly 9 digits (${digits.length}/9)`
  return undefined
}

// =============================================================================
// FORM INPUT (internal presentational component)
// =============================================================================

function FormInput({
  label,
  name,
  value,
  onChange,
  type = 'text',
  placeholder,
  disabled,
  icon,
  error,
  helpText,
}: FormInputProps) {
  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
        {label}
      </label>
      <div className="relative">
        {icon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
            {icon}
          </div>
        )}
        <input
          type={type}
          name={name}
          value={value}
          onChange={(e) => onChange(name, e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            'w-full px-3 py-2 rounded-lg text-sm transition-colors',
            'bg-white dark:bg-slate-800 border',
            error
              ? 'border-red-500 focus:ring-red-500/50'
              : 'border-slate-200 dark:border-slate-700 focus:ring-teal-500/50 focus:border-teal-500',
            'text-slate-900 dark:text-white placeholder-slate-400',
            'focus:outline-none focus:ring-2',
            icon && 'pl-10',
            disabled && 'opacity-60 cursor-not-allowed bg-slate-50 dark:bg-slate-900'
          )}
        />
      </div>
      {error && <p className="text-xs text-red-500">{error}</p>}
      {helpText && !error && <p className="text-xs text-slate-500 dark:text-slate-400">{helpText}</p>}
    </div>
  )
}

// =============================================================================
// FORM FIELD SECTIONS
// =============================================================================

export function OrgFormFields({ formData, errors, onChange, disabled }: OrgFormFieldsProps) {
  const abnError = useMemo(() => validateAbn(formData.abn), [formData.abn])
  const acnError = useMemo(() => validateAcn(formData.acn), [formData.acn])

  return (
    <>
      {/* Basic Info */}
      <div>
        <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">
          Basic Information
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            label="Organisation Name"
            name="name"
            value={formData.name || ''}
            onChange={onChange}
            placeholder="Enter organisation name"
            disabled={disabled}
            icon={<Building2 className="w-4 h-4" />}
            error={errors.name}
          />

          <FormInput
            label="Trading Name"
            name="company_name"
            value={formData.company_name || ''}
            onChange={onChange}
            placeholder="Trading as (optional)"
            disabled={disabled}
            icon={<Building2 className="w-4 h-4" />}
          />

          <FormInput
            label="ABN"
            name="abn"
            value={formData.abn || ''}
            onChange={onChange}
            placeholder="XX XXX XXX XXX"
            disabled={disabled}
            error={abnError}
            helpText={!abnError ? '11-digit Australian Business Number' : undefined}
          />

          <FormInput
            label="ACN"
            name="acn"
            value={formData.acn || ''}
            onChange={onChange}
            placeholder="XXX XXX XXX"
            disabled={disabled}
            error={acnError}
            helpText={!acnError ? '9-digit Australian Company Number (if applicable)' : undefined}
          />
        </div>
      </div>

      {/* Contact Details */}
      <div>
        <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">
          Contact Details
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            label="Email"
            name="email"
            type="email"
            value={formData.email || ''}
            onChange={onChange}
            placeholder="contact@company.com"
            disabled={disabled}
            icon={<Mail className="w-4 h-4" />}
          />

          <FormInput
            label="Phone"
            name="phone"
            type="tel"
            value={formData.phone || ''}
            onChange={onChange}
            placeholder="+61 2 1234 5678"
            disabled={disabled}
            icon={<Phone className="w-4 h-4" />}
          />

          <div className="md:col-span-2">
            <FormInput
              label="Website"
              name="website"
              type="url"
              value={formData.website || ''}
              onChange={onChange}
              placeholder="https://www.company.com"
              disabled={disabled}
              icon={<Globe className="w-4 h-4" />}
            />
          </div>
        </div>
      </div>

      {/* Address */}
      <div>
        <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">
          Address
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <FormInput
              label="Street Address"
              name="address"
              value={formData.address || ''}
              onChange={onChange}
              placeholder="123 Business St"
              disabled={disabled}
              icon={<MapPin className="w-4 h-4" />}
            />
          </div>

          <FormInput
            label="City"
            name="city"
            value={formData.city || ''}
            onChange={onChange}
            placeholder="Sydney"
            disabled={disabled}
          />

          <FormInput
            label="State"
            name="state"
            value={formData.state || ''}
            onChange={onChange}
            placeholder="NSW"
            disabled={disabled}
          />

          <FormInput
            label="Postcode"
            name="postcode"
            value={formData.postcode || ''}
            onChange={onChange}
            placeholder="2000"
            disabled={disabled}
          />

          <FormInput
            label="Country"
            name="country"
            value={formData.country || ''}
            onChange={onChange}
            placeholder="Australia"
            disabled={disabled}
          />
        </div>
      </div>
    </>
  )
}
